import { coinThrowToLine } from '../../core/iching-core';
import { ICHING_TRIGRAMS } from '../../core/iching-trigrams.const';
import {
  IChingCoinFace,
  IChingLinePosition,
  IChingMethodContent,
  IChingMethodLinePosition,
  IChingMethodTrigram,
  IChingReading,
  IChingTrigramId,
} from '../../core/iching.models';
import { IChingTranslate } from '../iching-consult-translate';

/**
 * De afleidingen achter de duiding (C2 bewegende lijnen, C3 de wortel, C5 de trigramwissel) als
 * pure functies. De leescomponenten én de Markdown-export van het dagboek bouwen erop, zodat een
 * export precies zegt wat het scherm zegt (spec 2026-10-21-dagboek-momenten-export-design).
 */

/** Eén bewegende lijn (C2): plaats, methode-uitleg, waarschuwing en waar de lijn vandaan komt. */
export interface IChingMovingRow {
  readonly position: IChingLinePosition;
  readonly method: IChingMethodLinePosition | undefined;
  readonly value: number;
  readonly yang: boolean;
  readonly warning: string | undefined;
  readonly symbol: string | undefined;
  /** Waar de lijn vandaan komt: de worp met zijn muntzijden, of enkel de waarde. */
  readonly origin: string;
}

/** Bewegende lijnen oplopend (de onderste eerst), met de waarschuwing die bij yang of yin hoort. */
export function buildMovingRows(
  reading: IChingReading,
  method: IChingMethodContent,
  coinFaces: ReadonlyArray<readonly IChingCoinFace[]>,
  t: IChingTranslate,
): IChingMovingRow[] {
  return [...reading.movingPositions]
    .sort((a, b) => a - b)
    .map(position => {
      const lineMethod = method.linePositions.find(p => p.position === position);
      const value = reading.lines[position - 1];
      const yang = value === 9;
      const moving = yang ? lineMethod?.yangMoving : lineMethod?.yinMoving;
      const faces = coinFaces[position - 1];
      // Alleen muntzijden die bij de lijnwaarde horen (een oude worp na een moduswissel telt niet).
      const throwFaces =
        faces?.length === 3 && coinThrowToLine(faces as [IChingCoinFace, IChingCoinFace, IChingCoinFace]) === value
          ? faces
          : undefined;
      return {
        position,
        method: lineMethod,
        value,
        yang,
        warning: moving?.warning,
        symbol: moving ? ICHING_TRIGRAMS[moving.trigram].symbol : undefined,
        origin: throwFaces
          ? t('humanDesign.iching.reading.movingFromThrow', { line: position, faces: faceLetters(throwFaces, t), value })
          : t('humanDesign.iching.reading.movingFromValue', { line: position, value }),
      };
    });
}

/** "K K K" of "M M M": eerste letter van kop en munt in de huidige taal. */
function faceLetters(faces: readonly IChingCoinFace[], t: IChingTranslate): string {
  return faces.map(face => t(`humanDesign.iching.cast.${face}`).charAt(0).toUpperCase()).join(' ');
}

export const LOWER_NUCLEAR_LINES: readonly IChingLinePosition[] = [2, 3, 4];
export const UPPER_NUCLEAR_LINES: readonly IChingLinePosition[] = [3, 4, 5];
export const LOWER_ENVELOPING_LINES: readonly IChingLinePosition[] = [1, 5, 6];
export const UPPER_ENVELOPING_LINES: readonly IChingLinePosition[] = [1, 2, 6];

/** Eén kerntrigram van de wortel (C3) met het trigram dat hem omhult. */
export interface IChingRootPair {
  readonly kind: 'upper' | 'lower';
  /** Lijnen van het hexagram waaruit dit kerntrigram komt. */
  readonly fromLines: readonly IChingLinePosition[];
  readonly nuclearId: IChingTrigramId;
  readonly nuclear: IChingMethodTrigram | undefined;
  /** Lijnen van het hexagram die het omhullende trigram vormen. */
  readonly envelopingLines: readonly IChingLinePosition[];
  readonly envelopingId: IChingTrigramId;
  readonly enveloping: IChingMethodTrigram | undefined;
  /** Het omhullende trigram is hetzelfde als het buitentrigram aan die kant. */
  readonly envelopedBySelf: boolean;
}

/** Het bovenste en het onderste kerntrigram, in die volgorde. */
export function buildRootPairs(reading: IChingReading, method: IChingMethodContent): IChingRootPair[] {
  const find = (id: IChingTrigramId): IChingMethodTrigram | undefined => method.trigrams.find(t => t.id === id);
  return [
    {
      kind: 'upper',
      fromLines: UPPER_NUCLEAR_LINES,
      nuclearId: reading.nuclearUpper,
      nuclear: find(reading.nuclearUpper),
      envelopingLines: UPPER_ENVELOPING_LINES,
      envelopingId: reading.envelopingUpper,
      enveloping: find(reading.envelopingUpper),
      envelopedBySelf: reading.envelopingUpper === reading.upper,
    },
    {
      kind: 'lower',
      fromLines: LOWER_NUCLEAR_LINES,
      nuclearId: reading.nuclearLower,
      nuclear: find(reading.nuclearLower),
      envelopingLines: LOWER_ENVELOPING_LINES,
      envelopingId: reading.envelopingLower,
      enveloping: find(reading.envelopingLower),
      envelopedBySelf: reading.envelopingLower === reading.lower,
    },
  ];
}

/** "2, 3 en 4" in de gegeven taal. */
export function listLines(lines: readonly number[], lang: string): string {
  return new Intl.ListFormat(lang, { style: 'long', type: 'conjunction' }).format(lines.map(String));
}

/** Een trigram van het gegooide hexagram in de omslag (C5): blijft het, of wisselt het. */
export interface IChingTrigramShiftRow {
  readonly kind: 'upper' | 'lower';
  readonly from: IChingTrigramId;
  readonly fromName: string;
  readonly to: IChingTrigramId;
  readonly toName: string;
  readonly changes: boolean;
}

export interface IChingTrigramShift {
  /** Boven eerst, zoals de trigrammen in de figuur liggen. */
  readonly rows: readonly [IChingTrigramShiftRow, IChingTrigramShiftRow];
  /** De lopende toelichting; leeg zolang niets wisselt. */
  readonly explanation: string | undefined;
}

/** Een kern langer dan dit wordt bij een komma ingekort, zodat hij tussen haakjes past. */
const CORE_MAX_LENGTH = 48;
/** Korter dan dit na inkorten zegt de kern te weinig: dan blijft het hele zinsdeel staan. */
const CORE_MIN_LENGTH = 20;

/**
 * De trigramwissel van de omslag met de lopende toelichting in drie delen: welk trigram
 * verandert en waarom (met wat het andere blijft), de rol van het wisselende trigram met de
 * kernen van en naar (jij voor je omgeving), en de vaste slotzin.
 */
export function buildTrigramShift(
  reading: IChingReading,
  method: IChingMethodContent,
  lang: string,
  t: IChingTranslate,
): IChingTrigramShift {
  const name = (id: IChingTrigramId): string => method.trigrams.find(trigram => trigram.id === id)?.name ?? ICHING_TRIGRAMS[id].pinyin;
  const row = (kind: IChingTrigramShiftRow['kind'], from: IChingTrigramId, to: IChingTrigramId): IChingTrigramShiftRow => ({
    kind,
    from,
    fromName: name(from),
    to,
    toName: name(to),
    changes: from !== to,
  });
  const upper = row('upper', reading.upper, reading.changedUpper ?? reading.upper);
  const lower = row('lower', reading.lower, reading.changedLower ?? reading.lower);
  if (!upper.changes && !lower.changes) {
    return { rows: [upper, lower], explanation: undefined };
  }

  const list = listLines(reading.movingPositions, lang);
  const lines = t(reading.movingPositions.length === 1 ? 'humanDesign.iching.reading.movingLineOne' : 'humanDesign.iching.reading.movingLinesMany', {
    list,
  });
  const changes =
    upper.changes && lower.changes
      ? t('humanDesign.iching.reading.shiftChangesBoth', { lines })
      : lower.changes
        ? t('humanDesign.iching.reading.shiftChangesLower', { lines, staysName: upper.fromName })
        : t('humanDesign.iching.reading.shiftChangesUpper', { lines, staysName: lower.fromName });
  const shortCore = (id: IChingTrigramId): string => {
    const core = method.trigrams.find(trigram => trigram.id === id)?.core ?? '';
    let clause = core.trim().split(/[.!?:;](?:\s|$)/)[0].trim();
    if (clause.length > CORE_MAX_LENGTH) {
      const comma = clause.lastIndexOf(',', CORE_MAX_LENGTH);
      if (comma >= CORE_MIN_LENGTH) {
        clause = clause.slice(0, comma);
      }
    }
    return clause.charAt(0).toLocaleLowerCase(lang) + clause.slice(1);
  };
  const roles = [lower, upper]
    .filter(shift => shift.changes)
    .map(shift =>
      t(shift.kind === 'lower' ? 'humanDesign.iching.reading.shiftRoleLower' : 'humanDesign.iching.reading.shiftRoleUpper', {
        fromTo: t('humanDesign.iching.reading.shiftFromTo', {
          fromName: shift.fromName,
          fromCore: shortCore(shift.from),
          toName: shift.toName,
          toCore: shortCore(shift.to),
        }),
      }),
    );
  return { rows: [upper, lower], explanation: [changes, ...roles, t('humanDesign.iching.reading.shiftClosing')].join(' ') };
}
